import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { mainSiteUrl } from '@/mocks/categories';
import { articleList } from '@/mocks/articles';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://cssbuy.pro';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const articleData = useMemo(() => {
    if (!slug) return undefined;
    return articleList.find((a) => a.slug === slug);
  }, [slug]);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (articleData) {
      document.title = `${articleData.title} | cssbuy.pro`;
    }
  }, [slug, articleData]);

  if (!articleData) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-foreground-600 mb-2">Artigo não encontrado.</p>
          <p className="text-xs text-foreground-400 mb-4">Slug: {slug || 'indefinido'}</p>
          <a href="/" className="text-accent-600 hover:text-accent-700">Voltar ao Início</a>
        </div>
      </div>
    );
  }

  const article = articleData;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    image: article.imageUrl,
    datePublished: '2026-06-01',
    dateModified: '2026-06-27',
    author: { '@type': 'Organization', name: 'CSSBuy Spreadsheet' },
    publisher: { '@type': 'Organization', name: 'CSSBuy Spreadsheet', url: siteUrl },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: article.faq.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Início', item: siteUrl },
      { '@type': 'ListItem', position: 2, name: article.title, item: `${siteUrl}/artigo/${slug}` },
    ],
  };

  const relatedArticles = articleList.filter((a) => a.slug !== slug).slice(0, 4);

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />

      <div className="min-h-screen bg-background-50">
        <nav className="sticky top-0 z-50 bg-background-50 border-b border-background-200/70">
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between h-14 md:h-16">
              <a href="/" className="text-lg font-heading font-bold text-foreground-950 whitespace-nowrap">
                cssbuy<span className="text-accent-500">.pro</span>
              </a>
              <div className="flex items-center gap-2 md:gap-3">
                <a
                  href="/"
                  className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 text-foreground-700 text-sm font-medium rounded-full hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-home-4-line"></i>
                  </span>
                  Início
                </a>
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-shopping-bag-line"></i>
                  </span>
                  Buy It Now
                </a>
              </div>
            </div>
          </div>
        </nav>

        <main className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
          <article>
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6 flex-wrap" aria-label="Breadcrumb">
              <a href="/" className="hover:text-accent-600 transition-colors">Início</a>
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-arrow-right-s-line"></i>
              </span>
              <span>Guias</span>
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-arrow-right-s-line"></i>
              </span>
              <span className="text-foreground-700 line-clamp-1">{article.title}</span>
            </nav>

            <header className="mb-10 md:mb-12">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 bg-accent-100 text-accent-700 text-xs rounded-full font-medium">
                  Guia SEO
                </span>
                <span className="text-xs text-foreground-400">Junho 2026</span>
                <span className="text-xs text-foreground-400">Equipe CSSBuy Spreadsheet</span>
              </div>
              <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-950 leading-tight mb-4">
                {article.title}
              </h1>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl">
                {article.description}
              </p>
            </header>

            <div className="mb-10">
              <img
                src={article.imageUrl}
                alt={article.title}
                title={article.title}
                className="w-full rounded-2xl object-cover object-top h-56 md:h-80"
              />
            </div>

            <div className="space-y-10 md:space-y-14">
              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                  {article.introTitle}
                </h2>
                {article.introParagraphs.map((p, i) => (
                  <p key={i} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4 last:mb-0">
                    {p}
                  </p>
                ))}
              </section>

              {article.sections.map((section, idx) => (
                <section key={idx}>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                    {section.title}
                  </h2>
                  {section.paragraphs.map((p, i) => (
                    <p key={i} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4 last:mb-0">
                      {p}
                    </p>
                  ))}
                  {section.list && (
                    <ul className="space-y-3 mt-4">
                      {section.list.map((item, i) => (
                        <li key={i} className="flex items-start gap-3 text-sm md:text-base text-foreground-700">
                          <span className="w-5 h-5 rounded-full bg-secondary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <i className="ri-check-line text-secondary-600 text-xs"></i>
                          </span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              ))}

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">
                  {article.comparison.title}
                </h2>
                <div className="overflow-x-auto rounded-xl border border-background-200/70">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-background-100">
                        <th className="text-left p-4 font-semibold text-foreground-900">{article.comparison.headers[0]}</th>
                        <th className="text-left p-4 font-semibold text-accent-700 bg-accent-50/40">{article.comparison.headers[1]}</th>
                        <th className="text-left p-4 font-semibold text-foreground-700">{article.comparison.headers[2]}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {article.comparison.rows.map((row, i) => (
                        <tr key={i} className="border-t border-background-200/70">
                          <td className="p-4 text-foreground-700 font-medium">{row.feature}</td>
                          <td className="p-4 text-foreground-900 bg-accent-50/20 font-medium">{row.us}</td>
                          <td className="p-4 text-foreground-600">{row.others}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">
                  Perguntas Frequentes
                </h2>
                <div className="space-y-4">
                  {article.faq.map((f, i) => (
                    <div key={i} className="bg-background-100 rounded-xl p-5 border border-background-200/70">
                      <h3 className="text-sm md:text-base font-semibold text-foreground-950 mb-2">{f.q}</h3>
                      <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{f.a}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section className="bg-secondary-50 border border-secondary-100 rounded-2xl p-6 md:p-8">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                  {article.conclusionTitle}
                </h2>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-6">
                  {article.conclusionParagraph}
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href={mainSiteUrl}
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-accent-500 text-background-50 text-sm font-semibold rounded-full hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {article.ctaLabel}
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-up-line"></i>
                    </span>
                  </a>
                  <a
                    href="/"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-background-200 bg-background-50 text-foreground-700 text-sm font-medium rounded-full hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-left-line"></i>
                    </span>
                    Voltar ao Início
                  </a>
                </div>
              </section>
            </div>
          </article>

          <aside className="mt-12 pt-8 border-t border-background-200/70">
            <h3 className="text-lg font-heading font-bold text-foreground-950 mb-5">
              Artigos Relacionados
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {relatedArticles.map((a) => (
                <a
                  key={a.slug}
                  href={`/artigo/${a.slug}`}
                  className="group flex gap-4 p-3 rounded-xl border border-background-200/70 bg-background-50 hover:border-accent-200 transition-colors"
                >
                  <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-background-100">
                    <img
                      src={a.imageUrl}
                      alt={a.title}
                      title={a.title}
                      className="w-full h-full object-cover object-top"
                      loading="lazy"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-semibold text-foreground-950 leading-snug line-clamp-2 group-hover:text-accent-700 transition-colors">
                      {a.title}
                    </h4>
                    <span className="inline-flex items-center gap-1 mt-2 text-xs text-accent-600 font-medium">
                      Ler artigo
                      <span className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-arrow-right-line"></i>
                      </span>
                    </span>
                  </div>
                </a>
              ))}
            </div>
            <div className="mt-6">
              <a href="/" className="inline-flex items-center gap-2 text-sm text-foreground-600 hover:text-accent-700 transition-colors">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-home-4-line"></i>
                </span>
                Voltar para a Página Inicial
              </a>
            </div>
          </aside>
        </main>

        <footer className="bg-secondary-800 text-background-50 py-8 px-4 md:px-6">
          <div className="max-w-4xl mx-auto text-center">
            <a href="/" className="text-lg font-heading font-bold text-background-50">
              cssbuy<span className="text-accent-500">.pro</span>
            </a>
            <p className="text-xs text-foreground-400 mt-2">
              © 2026 CSSBuy Spreadsheet. Todos os direitos reservados.
            </p>
          </div>
        </footer>
      </div>
    </>
  );
}